import { ErrorDetails } from '@utils';
import { RequestStatus } from '@store/types';

import { TestTypes } from '../../components/upload/TagPlate/constants';

export enum UploadStatus {
  none = 0,
  selected = 1 << 0,
  validating = 1 << 1,
  validationFailed = 1 << 2,
  validated = 1 << 3,
  uploadPending = 1 << 4,
  uploading = 1 << 5,
  uploadCancelled = 1 << 6,
  uploadInterrupted = 1 << 7,
  uploadFailed = 1 << 8,
  uploaded = 1 << 9,
  uploadCancelling = 1 << 10,
  uploadClaimed = 1 << 11,
  uploadRemoving = 1 << 12,
  all = ~(~0 << 13),
}

export const UploadStatusInProgress =
  UploadStatus.selected |
  UploadStatus.validating |
  UploadStatus.validated |
  UploadStatus.uploading;

export const UploadStatusSubmitted = UploadStatus.all ^ UploadStatus.selected;

export const UploadStatusValidated =
  UploadStatus.validated |
  UploadStatus.uploading |
  UploadStatus.uploadCancelled |
  UploadStatus.uploadInterrupted |
  UploadStatus.uploadFailed |
  UploadStatus.uploaded |
  UploadStatus.uploadCancelling;

export enum ValidationState {
  none,
  inProgress,
  passed,
  failed,
}

export interface IUpload {
  id: string;
  flowCellName: string;
  status: UploadStatus;
  path: string;
  uploadProgress: number;
  uploadStartDate: Date;
  uploadedBy: string;
  experimentListName: string;
  message?: string;
  experiment?: IExperimentResult;
  error?: string;
  transmitStarted?: Date;
  transmitEnded?: Date;
  isInFinalStage: boolean;
}

export enum ValidationWarningType {
  MRDMissingId, // mrd test is set for a subject that does not yet have any id tests
  MRDMissingIdClones, // mrd test is set for a subject id that has no id clones
}

export type IExperimentResult = MrdExperiment | NonMrdExperiment;

export type MrdExperiment = {
  kind: 'mrd';
} & Experiment<ITagPlate>;

type NonMrdExperiment = {
  kind: 'nonMrd';
} & Experiment<TagPlateNonMrd>;

export type Experiment<T> = {
  tagPlates: T[];
};

export interface ITagPlate {
  plateLayout: string;
  samples: ITagPlateSample[];
}

export type TagPlateNonMrd = {
  plateLayout: string;
  kitSN: string;
  samples: PlateSampleBase[];
};

export interface ITagPlateSample extends PlateSampleBase {
  externalSubjectId: string;
  testType: TestTypes;
  properties: IMrdStatus[] | undefined;
}

export type PlateSampleBase = {
  name: string;
  wells: ITagWell[];
  displayColor: string;
  // `undefined` is an accepted value for the `controlType` field for
  // backward compatibility. In the past, we used a missing
  // `controlType` field equivalent of the `none` value.
  controlType?: FlowcellControl;
};

export type enumProjectStatusKeys = 'SubjectMrdStatus';

export interface IMrdStatus {
  key: enumProjectStatusKeys;
  value: string;
}

export enum FlowcellControl {
  none,
  positive,
  negative,
}

export interface ITagWell {
  string: string;
  x: number;
  y: string;
}

export interface IExperimentList {
  status: ValidationState;
  errors: ErrorDetails[];
  experiment?: IExperimentResult;
  id: string;
  file: File;
  startUploadStatus: RequestStatus;
}
